import { Link } from "@tanstack/react-router";
import type { ProductRow } from "@shared/schemas/product";
import { Badge, type BadgeKind } from "@client/components/Badge";
import { formatRelative } from "@client/lib/datetime";

const STATUS_KIND: Record<ProductRow["status"], BadgeKind> = {
  ACTIVE: "positive",
  DRAFT: "neutral",
  ARCHIVED: "warning",
};

const STATUS_LABEL: Record<ProductRow["status"], string> = {
  ACTIVE: "Active",
  DRAFT: "Draft",
  ARCHIVED: "Archived",
};

/**
 * One product in the grid. The whole card is a link — a card you can only open
 * by hitting the four words of its title is a card people click twice.
 */
export function ProductCard({ product }: { product: ProductRow }) {
  return (
    <Link
      to="/products/$slug"
      params={{ slug: product.slug }}
      className="feature-card flex flex-col gap-3"
    >
      <div className="flex items-start justify-between gap-2">
        <h2 className="min-w-0 text-sm font-bold text-ink">{product.name}</h2>
        <Badge kind={STATUS_KIND[product.status]}>{STATUS_LABEL[product.status]}</Badge>
      </div>

      <p className="line-clamp-2 min-h-[2.5rem] text-xs text-ink-soft">
        {product.description ?? "No description."}
      </p>

      <dl className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs">
        <div className="flex items-baseline gap-1.5">
          <dt className="text-ink-soft">Scenarios</dt>
          <dd className="tabular font-semibold text-ink">{product.scenarioCount}</dd>
          {product.myScenarioCount > 0 && (
            <dd className="tabular text-ink-soft">({product.myScenarioCount} yours)</dd>
          )}
        </div>
        <div className="flex items-baseline gap-1.5">
          <dt className="text-ink-soft">Currency</dt>
          <dd className="font-semibold text-ink">{product.currencyCode}</dd>
        </div>
        <div className="flex items-baseline gap-1.5">
          <dt className="text-ink-soft">Horizon</dt>
          <dd className="tabular font-semibold text-ink">{product.defaultHorizonYears} yrs</dd>
        </div>
      </dl>

      <p className="mt-auto flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-ink-soft">
        <span>{product.owner.fullName}</span>
        <span aria-hidden="true">·</span>
        <span>updated {formatRelative(product.updatedAt)}</span>
      </p>
    </Link>
  );
}
